import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { HeroSearch } from "@/components/HeroSearch";
import { GroupDestinations } from "@/components/GroupDestinations";
import { Testimonials } from "@/components/Testimonials";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { BookingModal } from "@/components/BookingModal";
import { shareWhatsApp } from "@/lib/whatsapp";
import { MessageCircle, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";

const HERO = "https://images.unsplash.com/photo-1617007830823-91a153c420e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxCYWdoZGFkJTIwSXJhcSUyMGxhbmRtYXJrJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NzU4ODEwMnww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t, lang, money, settings } = useApp();
  useSeo({ title: t.seo.home[0], description: t.seo.home[1] });
  const [offers, setOffers] = useState([]);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    api.get("/offers").then((r) => setOffers(r.data));
  }, []);

  const shareOffer = (o) => {
    const msg = lang === "ar"
      ? `🔥 ${o.title_ar}\n${o.desc_ar}\n💰 ${money(o.price)} بدلاً من ${money(o.old_price)}\nسفير بغداد`
      : `🔥 ${o.title_en}\n${o.desc_en}\n💰 ${money(o.price)} instead of ${money(o.old_price)}\nSafeer Baghdad`;
    shareWhatsApp(msg);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 kenburns">
          <img src={settings.hero_image || HERO} alt="Iraq" className="w-full h-full object-cover" data-testid="hero-image" />
        </div>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-4xl mb-8">
            <span className="inline-block bg-gold/90 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-5">
              {t.tagline}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[3.4rem] font-extrabold text-white leading-tight mb-4 font-display lg:whitespace-nowrap" data-testid="hero-title">
              {settings[`hero_title_${lang}`] || t.hero.title}
            </h1>
            <p className="text-lg text-white/90 leading-relaxed" data-testid="hero-subtitle">{settings[`hero_sub_${lang}`] || t.hero.subtitle}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <HeroSearch />
          </motion.div>
        </div>
      </section>

      {/* GROUP DESTINATIONS */}
      <GroupDestinations />

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* OFFERS */}
      <section className="bg-tigris-dark py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">{t.sections.offersTitle}</h2>
              <p className="text-slate-300">{t.sections.offersSub}</p>
            </div>
            <Link to="/offers" className="hidden sm:flex items-center gap-1 text-gold font-semibold hover:gap-2 transition-all">
              {t.common.viewAll} <ArrowLeft size={18} className="rtl:rotate-0 ltr:rotate-180" />
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {offers.map((o, i) => (
              <motion.div key={o.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                data-testid={`offer-card-${o.id}`}
                className="bg-white rounded-2xl overflow-hidden card-hover flex flex-col">
                <div className="relative h-44">
                  <img src={o.image} alt={o[`title_${lang}`]} className="w-full h-full object-cover" />
                  <span className="absolute top-3 start-3 bg-clay text-white text-xs font-bold px-3 py-1 rounded-full" style={{ background: "var(--clay)" }}>
                    {o[`badge_${lang}`]}
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-bold text-lg text-slate-800 mb-1.5">{o[`title_${lang}`]}</h3>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-4 flex-1">{o[`desc_${lang}`]}</p>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl font-extrabold text-tigris">{money(o.price)}</span>
                    <span className="text-sm text-slate-400 line-through">{money(o.old_price)}</span>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`offer-book-${o.id}`} onClick={() => setBooking({ type: "package", item_id: o.ref_package, title: o[`title_${lang}`], amount: o.price, details: { offer_id: o.id } })}
                      className="btn-gold flex-1 py-2.5 rounded-xl font-bold text-sm">{t.common.bookNow}</button>
                    <button data-testid={`offer-share-${o.id}`} onClick={() => shareOffer(o)} className="btn-wa w-11 rounded-xl flex items-center justify-center">
                      <MessageCircle size={18} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16"><NewsletterSignup variant="home" /></section>
      {booking && <BookingModal item={booking} onClose={() => setBooking(null)} />}
    </div>
  );
}
